import { useCallback } from 'react';
import { useApplicationContext } from '../../context/ApplicationContext';
import { NavItem } from '../../config/navigationConfig.types';
import { getProgramUrl } from '../../utils/OptumPrograms';

/**
 * useNavRouting — resolves NavItem routes to application navigation actions.
 * Preserves identical logic from the original handleItemClick.
 * Also sets the active navigation item ID for visual state tracking.
 */
export function useNavRouting() {
  const {
    navigateToLanding,
    navigateToSection,
    navigateToComponent,
    openPresto,
    tenantId,
    setCurrentProgram,
    setActiveNavItem,
  } = useApplicationContext();

  const navigate = useCallback(
    (item: NavItem) => {
      // Set active nav item first
      setActiveNavItem(item.id);

      if (!item.route) return;

      switch (item.route.type) {
        case 'internal':
          navigateToLanding(item.route.path === '/' ? 'home' : item.id);
          break;

        case 'component':
          navigateToComponent(item.route.component as 'claims' | 'member');
          break;

        case 'program': {
          setCurrentProgram(item.route.program!);
          const url = getProgramUrl(item.route.program!, [], tenantId);
          if (url) {
            navigateToSection(url, false);
          }
          break;
        }

        case 'envKey': {
          const env = (window as any)['environments'];
          if (env?.[item.route.envKey!]) {
            const baseUrl: string = env[item.route.envKey!];
            const resolvedUrl = baseUrl + (item.route.envPath ?? '');
            if (resolvedUrl.startsWith('http://') || resolvedUrl.startsWith('https://')) {
              navigateToSection(resolvedUrl, false);
            }
          }
          break;
        }

        case 'external':
          if (item.id === 'presto') {
            openPresto();
          }
          break;
      }
    },
    [navigateToLanding, navigateToSection, navigateToComponent, openPresto, tenantId, setCurrentProgram, setActiveNavItem],
  );

  return { navigate };
}
